import type { WikiTreeNode } from '@sd/shared';
import { fetchJson } from './gm.js';

export interface WikiPageDto {
  slug: string;
  titleUk: string;
  titleEn: string;
  category: string;
  type: string;
  bodyMd: string;
  bodyHtml: string;
  related: string[];
  backlinks: Array<{ slug: string; titleUk: string }>;
  frontmatter: Record<string, unknown>;
  treePath: string;
  weight: number;
  updatedAt: number;
}

export interface SearchHit {
  slug: string;
  title: string;
  category: string;
  snippet: string;
}

export interface WriteInput {
  slug: string;
  titleUk: string;
  titleEn: string;
  category: string;
  type: string;
  bodyMd: string;
  related: string[];
  treePath: string;
  frontmatter: Record<string, unknown>;
}

export interface Revision {
  id: number;
  savedAt: number;
  bodyLength: number;
}

// ── Adventure DTOs ──────────────────────────────────────────────────────────

export interface AdventureMeta {
  id?: string;
  name?: string;
  nameEn?: string;
  subtitle?: string;
  levels?: string;
  tier?: number;
  type?: string;
  duration?: string;
  location?: string;
  thrMajor?: number;
  thrSevere?: number;
  order?: number;
}

export interface AdventureSummary {
  slug: string;
  order: number;
  meta: AdventureMeta;
}

export interface AdventureCharacter {
  id?: string;
  image?: string;
  name?: string;
  nameEn?: string;
  epithet?: string;
  icon?: string;
  tier?: number;
  type?: string;
  role?: 'antagonist' | 'ally' | 'optional' | 'neutral';
  boss?: boolean;
  description?: string;
  visual?: string;
  manner?: string;
  motivation?: string;
  stats?: {
    difficulty?: number;
    hp?: number;
    stress?: number;
    thresholdMajor?: number;
    thresholdSevere?: number;
    attackMod?: number;
  };
  attacks?: Array<{ name?: string; damage?: string; damageType?: string; description?: string }>;
  features?: Array<{ name?: string; description?: string }>;
  tactics?: string;
  /** Сцена взаємодії/діалогу/угоди з персонажем (HTML). */
  interaction?: string;
}

export interface AdventureLocation {
  id?: string;
  icon?: string;
  name?: string;
  sub?: string;
  image?: string;
  imageCaption?: string;
  sections?: Array<{ title?: string; body?: string }>;
}

export interface AdventureTimelineEvent {
  time?: string;
  title?: string;
  detail?: string;
  critical?: boolean;
  recent?: boolean;
}

export interface AdventureTreasure {
  id?: string;
  icon?: string;
  name?: string;
  type?: string;
  /** Опис у hub.html — поле `desc`. Підтримуємо також `description` про всяк випадок. */
  desc?: string;
  description?: string;
  /** Якщо є сторінка у вікі з повним описом — її slug */
  wikiSlug?: string;
  wikiTitle?: string;
  /** GM-only: запропонований slug для створення нової вікі-сторінки */
  suggestedSlug?: string;
}

export interface AdventureCombatGroup {
  label?: string;
  spec?: string;
}

export interface AdventureFull {
  slug: string;
  order: number;
  meta: AdventureMeta;
  characters: AdventureCharacter[];
  locations: AdventureLocation[];
  timeline: AdventureTimelineEvent[];
  treasures: AdventureTreasure[];
  combatGroups: AdventureCombatGroup[];
  endings?: unknown;
  images: Record<string, string>;
  overviewHTML?: string;
  flowHTML?: string;
  mapHTML?: string;
}

export const api = {
  health: () => fetchJson<{ ok: boolean; service: string; time: string }>('/api/health'),

  adventures: () => fetchJson<{ adventures: AdventureSummary[] }>('/api/adventures'),
  adventure: (slug: string) =>
    fetchJson<AdventureFull>(`/api/adventures/${encodeURIComponent(slug)}`),


  // ── Read ────────────────────────────────────────────────────────────────
  wikiTree: () => fetchJson<{ tree: WikiTreeNode[] }>('/api/wiki/tree'),
  wikiPage: (slug: string) =>
    fetchJson<WikiPageDto>(`/api/wiki/${encodeURIComponent(slug)}`),
  wikiSearch: (q: string, limit = 30) =>
    fetchJson<{ q: string; hits: SearchHit[] }>(
      `/api/wiki/search?q=${encodeURIComponent(q)}&limit=${limit}`,
    ),
  wikiSlugs: () => fetchJson<{ slugs: string[] }>('/api/wiki/slugs'),

  // ── Write (GM only) ─────────────────────────────────────────────────────
  wikiCreate: (input: WriteInput) =>
    fetchJson<{ ok: true; slug: string }>('/api/wiki', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  wikiUpdate: (slug: string, input: Partial<WriteInput>) =>
    fetchJson<{ ok: true; slug: string }>(`/api/wiki/${encodeURIComponent(slug)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  wikiRename: (slug: string, newSlug: string) =>
    fetchJson<{ ok: true; oldSlug: string; newSlug: string; inboundUpdated: number }>(
      `/api/wiki/${encodeURIComponent(slug)}/rename`,
      { method: 'PATCH', body: JSON.stringify({ newSlug }) },
    ),
  wikiDelete: (slug: string) =>
    fetchJson<{ ok: true; slug: string }>(`/api/wiki/${encodeURIComponent(slug)}`, {
      method: 'DELETE',
    }),
  wikiRevisions: (slug: string) =>
    fetchJson<{ slug: string; revisions: Revision[] }>(
      `/api/wiki/${encodeURIComponent(slug)}/revisions`,
    ),
  wikiRestore: (slug: string, revisionId: number) =>
    fetchJson<{ ok: true; slug: string }>(
      `/api/wiki/${encodeURIComponent(slug)}/restore/${revisionId}`,
      { method: 'POST' },
    ),

  // ── Encounters ──────────────────────────────────────────────────────────
  encounters: () =>
    fetchJson<{ encounters: Array<{ id: string; name: string; createdAt: number; active: boolean }> }>(
      '/api/encounters',
    ),
  encounterCreate: (name: string, mapCols = 20, mapRows = 14) =>
    fetchJson<{ encounter: { id: string; name: string } }>('/api/encounters', {
      method: 'POST',
      body: JSON.stringify({ name, mapCols, mapRows }),
    }),
  encounterJoinInfo: (id: string) =>
    fetchJson<{
      encounter: { id: string; name: string };
      players: Array<{ id: string; name: string; icon?: string }>;
    }>(`/api/encounters/${encodeURIComponent(id)}/join-info`),
  encounterJoin: (id: string, combatantId: string, displayName?: string) =>
    fetchJson<{ session: { id: string; combatantId: string; displayName: string } }>(
      `/api/encounters/${encodeURIComponent(id)}/join`,
      { method: 'POST', body: JSON.stringify({ combatantId, displayName }) },
    ),
  encounterImport: (data: unknown) =>
    fetchJson<{ ok: true; id: string }>('/api/encounters/import', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};
